import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { fmtMoney } from '../utils/currency';
import { renderCommentHtml } from '../utils/renderComment';
import { sanitizeHtml, hasRemoteImages } from '../utils/sanitize';
import { recordHref } from '../utils/recordLinks';
import { RecordLink } from '../components/RecordLink';
import QuotedReplyCollapse from '../components/QuotedReplyCollapse';
import ImageLightbox from '../components/ImageLightbox';
import EmbeddedImagesPanel from '../components/EmbeddedImagesPanel';
import { extractInlineImages, downloadInlineImage, hasUnresolvedImages } from '../utils/inlineImages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog.jsx';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RichTextEditor from '../components/RichTextEditor';
import { 
  User, Clock, MessageSquare, Send, AlertCircle, 
  CheckCircle, XCircle, Calendar, Mail, Phone, Tag,
  UserCog, Lock, Paperclip, Download, Trash2, Edit, Package, Save, X, DollarSign, Car, MapPin, Monitor,
  Timer, PlayCircle, StopCircle, ExternalLink, ArrowLeft, UserPlus, Users, ShieldBan, Ban, MoreVertical, ScanLine, FileText, Link2, Image as ImageIcon, AlertTriangle, ShieldAlert, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST, formatWithTimezone } from '../utils/timezone';
import { useAuth } from '../contexts/AuthContext';
import ScanAssetLookup from '../components/ScanAssetLookup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [promotedImageKeys, setPromotedImageKeys] = useState([]);
  const { user } = useAuth();
  // Session-only, per-message remote-image trust keyed by user+ticket+message.
  // In-memory only (NO localStorage): every new context defaults to blocked and no
  // trust is inherited across reloads or between messages. Persistent per-sender
  // trust/revoke is DEFERRED (not implemented this release).
  const [trustedMsgKeys, setTrustedMsgKeys] = useState(() => new Set());
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [convoSort, setConvoSort] = useState('newest'); // newest or oldest
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [billingData, setBillingData] = useState({
    is_billable: false,
    billable_hours: '',
    hourly_rate: '',
    billable_amount: '',
    mileage: '',
    mileage_rate: '',
    mileage_billable: false,
    mileage_amount: ''
  });
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const fileInputRef = useRef(null);
  const [ccContacts, setCcContacts] = useState([]);
  const [ccEmail, setCcEmail] = useState('');
  const [ccName, setCcName] = useState('');
  const [ccAdding, setCcAdding] = useState(false);
  const [linkedAssets, setLinkedAssets] = useState([]);
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);

  useEffect(() => {
    if (ticketId) {
      loadTicketData();
    }
  }, [ticketId]);

  const loadTicketData = async () => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setTicket(response.data);
      setPromotedImageKeys(response.data?.promoted_image_keys || []);
      setPageLoading(false);
    } catch (error) {
      console.error('Failed to load ticket:', error);
      toast.error('Failed to load ticket');
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (ticket) {
      loadComments();
      loadAttachments();
      loadAssets();
      loadCustomers();
      loadBoards();
      loadTimeEntries();
      loadTechnicians();
      loadCcContacts();
      loadLinkedAssets();
      loadLinkedInvoices();
      loadAllInvoices();
      setEditData({
        title: ticket.title,
        description: ticket.description || '',
        customer_id: ticket.customer_id,
        contact_person: ticket.contact_person || '',
        contact_email: ticket.contact_email || '',
        contact_phone: ticket.contact_phone || '',
        category: ticket.category || 'other',
        asset_id: ticket.asset_id || '',
        support_type: ticket.support_type || 'remote',
        board_id: ticket.board_id || ''
      });
      setBillingData({
        is_billable: ticket.is_billable || false,
        billable_hours: ticket.billable_hours?.toString() || '',
        hourly_rate: ticket.hourly_rate?.toString() || '',
        billable_amount: ticket.billable_amount?.toString() || '',
        mileage: ticket.mileage?.toString() || '',
        mileage_rate: ticket.mileage_rate?.toString() || '0.67',
        mileage_billable: ticket.mileage_billable || false,
        mileage_amount: ticket.mileage_amount?.toString() || ''
      });
    }
  }, [open, ticket]);

  const loadComments = async () => {
    if (!ticket) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/tickets/${ticket.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async () => {
    if (!ticket) return;
    
    try {
      const response = await api.get(`/tickets/${ticket.id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to load attachments');
    }
  };

  const loadAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets');
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const loadBoards = async () => {
    try {
      const response = await api.get('/ticket-boards');
      setBoards(response.data.filter(b => b.is_active));
    } catch (error) {
      console.error('Failed to load boards');
    }
  };


  const loadTechnicians = async () => {
    try {
      const response = await api.get('/users');
      // Filter to only admins and technicians
      const staff = response.data.filter(u => u.role === 'admin' || u.role === 'technician');
      setTechnicians(staff);
    } catch (error) {
      console.error('Failed to load technicians');
    }
  };


  const loadTimeEntries = async () => {
    if (!ticket) return;
    
    try {
      const response = await api.get(`/tickets/${ticket.id}/time-entries`);
      setTimeEntries(response.data);
      
      // Check for active timer
      const active = response.data.find(e => !e.end_time && e.technician_email === user?.email);
      setActiveTimer(active || null);
    } catch (error) {
      console.error('Failed to load time entries');
    }
  };

  const loadCcContacts = async () => {
    if (!ticket) return;
    try {
      const response = await api.get(`/tickets/${ticket.id}/cc`);
      setCcContacts(response.data);
    } catch (error) {
      console.error('Failed to load CC contacts');
    }
  };

  const handleAddCc = async () => {
    if (!ccEmail.trim()) return;
    setCcAdding(true);
    try {
      await api.post(`/tickets/${ticket.id}/cc`, { email: ccEmail.trim(), name: ccName.trim() });
      toast.success('CC contact added');
      setCcEmail('');
      setCcName('');
      loadCcContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add CC contact');
    } finally {
      setCcAdding(false);
    }
  };

  const handleRemoveCc = async (email) => {
    try {
      await api.delete(`/tickets/${ticket.id}/cc/${encodeURIComponent(email)}`);
      toast.success('CC contact removed');
      loadCcContacts();
    } catch (error) {
      toast.error('Failed to remove CC contact');
    }
  };

  const loadLinkedAssets = async () => {
    if (!ticket) return;
    try {
      const response = await api.get(`/tickets/${ticket.id}/assets`);
      setLinkedAssets(response.data);
    } catch { setLinkedAssets([]); }
  };

  const handleAddLinkedAsset = async (assetId) => {
    try {
      await api.post(`/tickets/${ticket.id}/assets`, { asset_id: assetId });
      toast.success('Asset linked');
      loadLinkedAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link asset');
    }
  };

  const handleRemoveLinkedAsset = async (assetId) => {
    try {
      await api.delete(`/tickets/${ticket.id}/assets/${assetId}`);
      toast.success('Asset unlinked');
      loadLinkedAssets();
    } catch { toast.error('Failed to unlink asset'); }
  };

  const loadLinkedInvoices = async () => {
    if (!ticket) return;
    try {
      const response = await api.get(`/tickets/${ticket.id}/invoices`);
      setLinkedInvoices(response.data);
    } catch { setLinkedInvoices([]); }
  };

  const loadAllInvoices = async () => {
    try {
      const response = await api.get('/invoices', { params: { limit: 5000 } });
      const d = response.data;
      setAllInvoices(Array.isArray(d) ? d : d.invoices || []);
    } catch { setAllInvoices([]); }
  };

  const handleAddLinkedInvoice = async (invoiceId) => {
    try {
      await api.post(`/tickets/${ticket.id}/invoices`, { invoice_id: invoiceId });
      toast.success('Invoice linked');
      loadLinkedInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link invoice');
    }
  };

  const handleRemoveLinkedInvoice = async (invoiceId) => {
    try {
      await api.delete(`/tickets/${ticket.id}/invoices/${invoiceId}`);
      toast.success('Invoice unlinked');
      loadLinkedInvoices();
    } catch { toast.error('Failed to unlink invoice'); }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/export/tickets/${ticket.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${ticket.ticket_number || ticket.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export PDF'); }
  };

  const [resendingUpdate, setResendingUpdate] = useState(false);
  const handleResendLastUpdate = async () => {
    if (!ticket?.contact_email) { toast.error('No contact email on this ticket'); return; }
    setResendingUpdate(true);
    try {
      // Get the latest comment
      const commentsRes = await api.get(`/tickets/${ticket.id}/comments`);
      const comments = commentsRes.data || [];
      const lastComment = [...comments].reverse().find(c => c.comment_type !== 'system');
      if (!lastComment) { toast.error('No comments to resend'); setResendingUpdate(false); return; }
      // Re-post as a notification trigger by adding a system note
      await api.post(`/tickets/${ticket.id}/comments`, {
        comment_text: `[Resent notification] ${lastComment.comment_text}`,
        is_internal: false,
      });
      toast.success('Last update resent to customer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    }
    setResendingUpdate(false);
  };

  const handleClockIn = async () => {
    try {
      await api.post(`/tickets/${ticket.id}/time-entries/clock-in`, {
        is_billable: billingData.is_billable
      });
      
      toast.success('Timer started');
      loadTimeEntries();
      loadTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handleClockOut = async () => {
    if (!activeTimer) return;
    
    try {
      await api.put(`/tickets/${ticket.id}/time-entries/${activeTimer.id}/clock-out`);
      
      toast.success('Timer stopped and logged');
      setActiveTimer(null);
      setElapsedTime(0);
      loadTimeEntries();
      loadTicketData();
    } catch (error) {
      toast.error('Failed to stop timer');
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await api.delete(`/tickets/${ticket.id}/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      loadTimeEntries();
      loadTicketData();
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  // Timer effect - update elapsed time every second
  useEffect(() => {
    if (!activeTimer) {
      setElapsedTime(0);
      return;
    }

    const startTime = new Date(activeTimer.start_time);
    
    const interval = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now - startTime) / 1000); // seconds
      setElapsedTime(elapsed);
    }, 1000);

    if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Ticket not found</div>
      </div>
    );
  }

  return () => clearInterval(interval);
  }, [activeTimer]);

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };


  const [showInvoicePicker, setShowInvoicePicker] = useState(false);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [attachingInvoice, setAttachingInvoice] = useState(false);

  const loadCustomerInvoices = async () => {
    if (!ticket?.customer_id) return;
    try {
      const r = await api.get('/invoices', { params: { customer_id: ticket.customer_id, limit: 100 } });
      setCustomerInvoices(r.data?.invoices || r.data || []);
    } catch(err) { console.error("Error:", err); }
  };

  const handleAttachInvoicePdf = async (invoiceId, invoiceNumber) => {
    setAttachingInvoice(true);
    try {
      // Download the PDF as blob
      const pdfRes = await api.get(`/export/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const filename = `Invoice-${invoiceNumber || invoiceId}.pdf`;
      
      // Upload as ticket attachment
      const formData = new FormData();
      formData.append('file', new Blob([pdfRes.data], { type: 'application/pdf' }), filename);
      await api.post(`/tickets/${ticket.id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      
      // Also add a comment noting the attachment
      await api.post(`/tickets/${ticket.id}/comments`, {
        comment_text: `<p>Attached invoice <strong>#${invoiceNumber}</strong> (PDF)</p>`,
        is_internal: isInternal,
      });
      
      toast.success(`Invoice #${invoiceNumber} attached`);
      loadComments();
      loadAttachments();
      setShowInvoicePicker(false);
    } catch (err) {
      toast.error('Failed to attach invoice: ' + (err.response?.data?.detail || err.message));
    }
    setAttachingInvoice(false);
  };

  const [originalEmail, setOriginalEmail] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleViewOriginalEmail = async () => {
    try {
      const res = await api.get(`/emails/${ticket.email_message_id}`);
      setOriginalEmail(res.data);
      setEmailModalOpen(true);
    } catch {
      toast.error('Failed to load original email');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    // Check for empty content (TipTap returns <p></p> for empty)
    const isEmpty = !newComment || newComment === '<p></p>' || newComment.replace(/<[^>]*>/g, '').trim() === '';
    if (isEmpty) return;

    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticket.id}/comments`, {
        comment_text: newComment,
        is_internal: isInternal
      });
      
      toast.success('Comment added successfully');
      setNewComment('');
      setIsInternal(false);
      loadComments();
      
      loadTicketData();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/tickets/${ticket.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      loadAttachments();
      loadTicketData();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/tickets/${ticket.id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await api.delete(`/tickets/${ticket.id}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      loadAttachments();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/tickets/${ticket.id}`, { status: newStatus });
      toast.success('Status updated successfully');
      loadTicketData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      await api.put(`/tickets/${ticket.id}`, { priority: newPriority });
      toast.success('Priority updated successfully');
      loadTicketData();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };


  const handleAssignmentChange = async (userId) => {
    try {
      const technician = technicians.find(t => t.id === userId);
      await api.put(`/tickets/${ticket.id}`, { 
        assigned_to: userId === 'unassigned' ? null : userId,
        assigned_to_name: userId === 'unassigned' ? null : technician?.full_name
      });
      toast.success('Ticket assigned successfully');
      loadTicketData(); // Reload to get updated data
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };


  const handleBillingUpdate = async () => {
    try {
      const billingPayload = {
        is_billable: billingData.is_billable,
        billable_hours: billingData.billable_hours ? parseFloat(billingData.billable_hours) : null,
        hourly_rate: billingData.hourly_rate ? parseFloat(billingData.hourly_rate) : null,
        billable_amount: billingData.billable_amount ? parseFloat(billingData.billable_amount) : null,
        mileage: billingData.mileage ? parseFloat(billingData.mileage) : null,
        mileage_rate: billingData.mileage_rate ? parseFloat(billingData.mileage_rate) : null,
        mileage_billable: billingData.mileage_billable,
        mileage_amount: billingData.mileage_amount ? parseFloat(billingData.mileage_amount) : null
      };

      await api.put(`/tickets/${ticket.id}`, billingPayload);
      toast.success('Billing information updated');
      loadTicketData();
    } catch (error) {
      toast.error('Failed to update billing');
    }
  };

  const handleCreateInvoiceFromBilling = async () => {
    try {
      // Build line items from billing data
      const items = [];
      const hours = parseFloat(billingData.billable_hours) || 0;
      const rate = parseFloat(billingData.hourly_rate) || 0;
      if (hours > 0 && rate > 0) {
        items.push({
          description: `Support: ${ticket.title} (Ticket #${ticket.ticket_number})`,
          quantity: hours,
          unit_price: rate,
          amount: Math.floor(hours * rate * 100) / 100,
          tax_exempt: false,
        });
      }
      if (billingData.mileage_billable) {
        const miles = parseFloat(billingData.mileage) || 0;
        const mileRate = parseFloat(billingData.mileage_rate) || 0;
        if (miles > 0 && mileRate > 0) {
          items.push({
            description: `Mileage: On-site visit for Ticket #${ticket.ticket_number}`,
            quantity: miles,
            unit_price: mileRate,
            amount: Math.floor(miles * mileRate * 100) / 100,
            tax_exempt: true,
          });
        }
      }
      if (items.length === 0) {
        toast.error('No billable items to invoice. Set hours/rate first.');
        return;
      }
      const subtotal = items.reduce((s, i) => s + i.amount, 0);
      const invoicePayload = {
        customer_id: ticket.customer_id,
        customer_name: ticket.customer_name || '',
        items,
        subtotal,
        tax_amount: 0,
        total: subtotal,
        status: 'draft',
        notes: `Generated from Ticket #${ticket.ticket_number}: ${ticket.title}`,
        source: 'ticket_billing',
        source_ticket_id: ticket.id,
      };
      const res = await api.post('/invoices', invoicePayload);
      const newInvoice = res.data;
      // Link invoice to ticket
      try {
        await api.post(`/tickets/${ticket.id}/invoices`, { invoice_id: newInvoice.id });
      } catch { /* linking is optional */ }
      toast.success(`Invoice #${newInvoice.invoice_number || newInvoice.id} created`);
      navigate(`/invoices/${newInvoice.id}`);
    } catch (error) {
      console.error('Invoice creation failed:', error);
      toast.error('Failed to create invoice: ' + (error.response?.data?.detail || error.message));
    }
  };

  const calculateBillableAmount = () => {
    const hours = parseFloat(billingData.billable_hours) || 0;
    const rate = parseFloat(billingData.hourly_rate) || 0;
    return (hours * rate).toFixed(2);
  };

  const calculateMileageAmount = () => {
    const miles = parseFloat(billingData.mileage) || 0;
    const rate = parseFloat(billingData.mileage_rate) || 0;
    return (miles * rate).toFixed(2);
  };

  const calculateTotalBillable = () => {
    const timeAmount = parseFloat(billingData.billable_amount) || 0;
    const mileageAmount = billingData.mileage_billable ? (parseFloat(billingData.mileage_amount) || 0) : 0;
    return (timeAmount + mileageAmount).toFixed(2);
  };

  useEffect(() => {
    if (billingData.billable_hours && billingData.hourly_rate) {
      const calculated = calculateBillableAmount();
      if (calculated !== billingData.billable_amount) {
        setBillingData(prev => ({ ...prev, billable_amount: calculated }));
      }
    }
  }, [billingData.billable_hours, billingData.hourly_rate]);

  useEffect(() => {
    if (billingData.mileage && billingData.mileage_rate) {
      const calculated = calculateMileageAmount();
      if (calculated !== billingData.mileage_amount) {
        setBillingData(prev => ({ ...prev, mileage_amount: calculated }));
      }
    }
  }, [billingData.mileage, billingData.mileage_rate]);

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Ticket not found</div>
      </div>
    );
  }

  const handleSaveEdit = async () => {
    try {
      // Get customer name if customer_id changed
      const customer = customers.find(c => c.id === editData.customer_id);
      const updatePayload = {
        ...editData,
        customer_name: customer?.company_name || ticket.customer_name
      };
      
      // Get asset name if asset_id is set
      if (editData.asset_id) {
        const asset = assets.find(a => a.id === editData.asset_id);
        updatePayload.asset_name = asset?.name || '';
      }

      await api.put(`/tickets/${ticket.id}/full-edit`, updatePayload);
      toast.success('Ticket updated successfully');
      setEditMode(false);
      loadTicketData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleBlockSender = async () => {
    if (!ticket.contact_email) {
      toast.error('This ticket has no contact email to block');
      return;
    }
    if (!window.confirm(`Block sender "${ticket.contact_email}" and close this ticket?`)) return;
    try {
      await api.post(`/tickets/${ticket.id}/block-sender`, { reason: 'blocked' });
      toast.success(`Sender ${ticket.contact_email} blocked and ticket closed`);
      loadTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block sender');
    }
  };

  const handleMarkSpam = async (reason, blockSender = false) => {
    const label = reason === 'spam' ? 'spam' : 'unsolicited';
    if (!window.confirm(`Mark this ticket as ${label}${blockSender ? ' and block sender' : ''}? The ticket will be closed.`)) return;
    try {
      await api.post(`/tickets/${ticket.id}/mark-spam`, { reason, block_sender: blockSender });
      toast.success(`Ticket marked as ${label}${blockSender ? ' and sender blocked' : ''}`);
      loadTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark ticket');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Ticket not found</div>
      </div>
    );
  }

  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!ticket) return null;

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.open;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="h-4 w-4" />,
      in_progress: <Clock className="h-4 w-4" />,
      resolved: <CheckCircle className="h-4 w-4" />,
      closed: <XCircle className="h-4 w-4" />
    };
    return icons[status] || icons.open;
  };

  const isAdmin = user?.role === 'admin';
  const isAdminOrTech = user?.role === 'admin' || user?.role === 'technician';

  // Open embedded content images in a lightbox (ignores placeholders/UI icons).
  const handleContentImageClick = (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG') {
      const src = t.getAttribute('src') || '';
      if (src && !src.startsWith('data:image/svg')) {
        e.preventDefault();
        e.stopPropagation();
        const idx = inlineImages.findIndex((im) => im.src === src);
        if (idx >= 0) {
          setLightbox({ images: inlineImages, index: idx });
        } else {
          setLightbox({ images: [{ src, name: t.getAttribute('alt') || 'Image' }], index: 0 });
        }
      }
    }
  };

  // Collect embedded images + detect unresolved (cid:) images across ticket body + comments.
  const bodyHtmls = [
    ticket.description_html || ticket.description || '',
    ...comments.map((c) => c.comment_text || ''),
  ];
  const inlineImages = extractInlineImages(bodyHtmls);
  const hasUnresolvedInlineImages = hasUnresolvedImages(bodyHtmls);
  const descriptionHasRemote = hasRemoteImages([ticket.description_html || ticket.description || '']);

  const msgTrustKey = (messageId) => `${user?.email || 'anon'}::${ticketId}::${messageId}`;
  const isRemoteBlocked = (messageId) => !trustedMsgKeys.has(msgTrustKey(messageId));
  const toggleMessageImages = (messageId, load) => {
    setTrustedMsgKeys((prev) => {
      const next = new Set(prev);
      const k = msgTrustKey(messageId);
      if (load) next.add(k); else next.delete(k);
      return next;
    });
  };

  const handlePromoteImage = async (key, promoted) => {
    const prev = promotedImageKeys;
    setPromotedImageKeys((k) => (promoted ? [...new Set([...k, key])] : k.filter((x) => x !== key)));
    try {
      const res = await api.post(`/tickets/${ticket.id}/embedded-images/promote`, { key, promoted });
      if (res.data?.promoted_image_keys) setPromotedImageKeys(res.data.promoted_image_keys);
    } catch (e) {
      setPromotedImageKeys(prev);
      toast.error('Could not update image');
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Ticket not found</div>
      </div>
    );
  }

  return (
    <div className="co-scope co-settings ticket-ui min-h-screen bg-[#041019]">
      <div className="w-full px-8 py-6" data-testid="ticket-detail-page">
        {/* Back button outside the container */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="ticket-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {descriptionHasRemote && isRemoteBlocked('description') && (
          <div
            data-testid="ticket-image-privacy-banner"
            className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-200"
          >
            <span className="flex items-start sm:items-center gap-2">
              <ShieldAlert className="h-4 w-4 mt-0.5 sm:mt-0 shrink-0" />
              External images in the original message are hidden to protect your privacy — they can reveal when and where a message is opened.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => toggleMessageImages('description', true)}
              data-testid="ticket-load-images-btn"
              className="shrink-0 border-amber-500/50 text-amber-100 hover:bg-amber-500/20"
            >
              Load pictures
            </Button>
          </div>
        )}
        {descriptionHasRemote && !isRemoteBlocked('description') && (
          <div data-testid="ticket-images-trusted-note" className="mb-4 flex items-center gap-2 text-xs text-gray-400">
            <ShieldCheck className="h-3.5 w-3.5" /> External images are shown for the original message (this session only).
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toggleMessageImages('description', false)}
              data-testid="ticket-hide-images-btn"
              className="h-6 px-2 text-xs"
            >
              Hide pictures
            </Button>
          </div>
        )}


        <div className="bg-[#081a25] border border-[#193b4b] rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs sm:text-sm font-mono text-muted-foreground">{ticket.ticket_number}</span>
              <span className="text-lg sm:text-xl font-bold">{ticket.title}</span>
              {ticket.source && ticket.source !== 'manual' && (
                <Badge variant="outline" className={`text-xs ${
                  ticket.source === 'ninjaone' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                  ticket.source === 'ninjarmm_webhook' ? 'border-orange-300 text-orange-700 bg-orange-50' :
                  ticket.source === 'office365_email' ? 'border-purple-300 text-purple-700 bg-purple-50' :
                  'border-gray-300 text-gray-600'
                }`} data-testid="ticket-source-badge">
                  {ticket.source === 'ninjaone' ? 'NinjaOne' :
                   ticket.source === 'ninjarmm_webhook' ? 'NinjaOne Alert' :
                   ticket.source === 'office365_email' ? 'Email' :
                   ticket.source}
                </Badge>
              )}
              {ticket.provider_deep_link && (
                <a href={ticket.provider_deep_link} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                   data-testid="ticket-provider-link">
                  <ExternalLink className="h-3 w-3" /> Open in NinjaOne
                </a>
              )}
              {ticket.spam_status && (
                <Badge variant="destructive" className="text-xs" data-testid="spam-badge">
                  {ticket.spam_status === 'spam' ? 'Spam' : ticket.spam_status === 'unsolicited' ? 'Unsolicited' : 'Blocked'}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">

              {isAdmin && !editMode && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="ticket-actions-menu">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="co-scope ticket-ui bg-[#081a25] text-[#f4f8fa] border-[#193b4b] w-56">
                    <DropdownMenuItem onClick={() => setEditMode(true)} className="cursor-pointer" data-testid="action-edit">
                      <Edit className="h-4 w-4 mr-2" /> Edit Ticket
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleMarkSpam('spam', false)} className="cursor-pointer text-orange-600" data-testid="action-mark-spam">
                      <ShieldBan className="h-4 w-4 mr-2" /> Mark as Spam
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleMarkSpam('unsolicited', false)} className="cursor-pointer text-orange-600" data-testid="action-mark-unsolicited">
                      <ShieldBan className="h-4 w-4 mr-2" /> Mark as Unsolicited
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleMarkSpam('spam', true)} className="cursor-pointer text-red-600" data-testid="action-spam-block">
                      <Ban className="h-4 w-4 mr-2" /> Spam & Block Sender
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleBlockSender} className="cursor-pointer text-red-600" data-testid="action-block-sender">
                      <Ban className="h-4 w-4 mr-2" /> Block Sender
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {isAdmin && !editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-ticket-button">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {!editMode && (
                <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="export-ticket-pdf-btn">
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </Button>
              )}
              {!editMode && ticket?.contact_email && (
                <Button variant="outline" size="sm" onClick={handleResendLastUpdate} disabled={resendingUpdate} data-testid="resend-ticket-update-btn">
                  {resendingUpdate ? <Timer className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Resend Update
                </Button>
              )}
              {editMode && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit} data-testid="save-ticket-button">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {hasUnresolvedInlineImages && (
          <div data-testid="unresolved-images-warning" className="flex items-start gap-2 px-4 py-3 mb-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Some embedded images in this ticket couldn't be loaded. An admin can re-fetch them
              from <strong>Office 365 Settings → Repair Embedded Images</strong>.
            </span>
          </div>
        )}

        <Tabs defaultValue="conversation" className="space-y-4 ticket-content" onClick={handleContentImageClick}>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-max md:w-auto">
            <TabsTrigger value="conversation" data-testid="tab-conversation">Conversation ({comments.length + ((ticket?.description || ticket?.description_html) ? 1 : 0)})</TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">Attachments ({attachments.length + inlineImages.length})</TabsTrigger>
            {isAdminOrTech && <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>}
            {isAdminOrTech && <TabsTrigger value="mileage" data-testid="tab-mileage">Mileage</TabsTrigger>}
          </TabsList>
          </div>

          <TabsContent value="details" className="space-y-6">
            {editMode ? (
              /* EDIT MODE */
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                    data-testid="edit-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    rows={4}
                    data-testid="edit-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Customer *</Label>
                    <Select
                      value={editData.customer_id}
                      onValueChange={(value) => setEditData({...editData, customer_id: value})}
                    >
                      <SelectTrigger data-testid="edit-customer-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Linked Assets</Label>
                    <div className="flex items-center gap-2">
                    <Select
                      value=""
                      onValueChange={(assetId) => handleAddLinkedAsset(assetId)}
                    >
                      <SelectTrigger data-testid="edit-asset-select">
                        <SelectValue placeholder="Add asset" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        {assets.filter(a => !linkedAssets.find(la => la.asset_id === a.id)).map((asset) => (
                          <SelectItem key={asset.id} value={asset.id}>
                            {asset.name} ({asset.serial_number || 'N/A'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ScanAssetLookup
                      onAssetFound={(asset) => handleAddLinkedAsset(asset.id)}
                      customerId={editData?.customer_id || undefined}
                      customers={customers}
                      alreadyLinkedIds={linkedAssets.map(la => la.asset_id)}
                      buttonVariant="outline"
                      buttonSize="icon"
                    />
                    </div>
                    {linkedAssets.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="edit-asset-chips">
                        {linkedAssets.map((la) => (
                          <span key={la.asset_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <span className={`h-1.5 w-1.5 rounded-full ${({active:'bg-emerald-500',maintenance:'bg-amber-500',inactive:'bg-gray-400',retired:'bg-red-500'})[la.status] || 'bg-gray-400'}`} />
                            <Package className="h-3 w-3" />
                            {la.asset_name}
                            <button type="button" onClick={() => handleRemoveLinkedAsset(la.asset_id)} className="hover:text-red-600" data-testid={`edit-asset-remove-${la.asset_id}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Linked Invoices */}
                <div>
                  <Label>Linked Invoices</Label>
                  <Select
                    value=""
                    onValueChange={(invoiceId) => handleAddLinkedInvoice(invoiceId)}
                  >
                    <SelectTrigger data-testid="edit-invoice-select">
                      <SelectValue placeholder="Link an invoice" />
                    </SelectTrigger>
                    <SelectContent className="co-scope ticket-ui">
                      {allInvoices.filter(inv => !linkedInvoices.find(li => li.invoice_id === inv.id)).map((inv) => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.invoice_number} - {inv.customer_name || ''} (${(inv.total || 0).toFixed(2)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {linkedInvoices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="edit-invoice-chips">
                      {linkedInvoices.map((li) => (
                        <span key={li.invoice_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                          <FileText className="h-3 w-3" />
                          {li.invoice_number} (${(li.total || 0).toFixed(2)})
                          <button type="button" onClick={() => handleRemoveLinkedInvoice(li.invoice_id)} className="hover:text-red-600" data-testid={`edit-invoice-remove-${li.invoice_id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Contact Person</Label>
                    <Input
                      value={editData.contact_person}
                      onChange={(e) => setEditData({...editData, contact_person: e.target.value})}
                      data-testid="edit-contact-person-input"
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={editData.contact_email}
                      onChange={(e) => setEditData({...editData, contact_email: e.target.value})}
                      data-testid="edit-contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={editData.contact_phone}
                      onChange={(e) => setEditData({...editData, contact_phone: e.target.value})}
                      data-testid="edit-contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={editData.category}
                      onValueChange={(value) => setEditData({...editData, category: value})}
                    >
                      <SelectTrigger data-testid="edit-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="hardware">Hardware</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="network">Network</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="font-semibold">Support Type</Label>
                    <Select
                      value={editData.support_type}
                      onValueChange={(value) => setEditData({...editData, support_type: value})}
                    >
                      <SelectTrigger data-testid="edit-support-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="remote">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            <span>Remote Support</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="onsite">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>On-Site Visit</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="hybrid">Hybrid (Remote + On-Site)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Ticket Board</Label>
                    <Select
                      value={editData.board_id || undefined}
                      onValueChange={(value) => setEditData({...editData, board_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger data-testid="edit-board-select">
                        <SelectValue placeholder="No board (optional)" />
                      </SelectTrigger>
                      <SelectContent className="co-scope ticket-ui">
                        <SelectItem value="none">No Board</SelectItem>
                        {boards.map(board => (
                          <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              /* VIEW MODE */
              <>
                {/* Ticket Header Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold">Customer:</span>
                          <span>{ticket.customer_name || 'N/A'}</span>
                        </div>
                        {ticket.contact_person && (
                          <div className="flex items-center gap-2 text-sm">
                            <UserCog className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold">Contact:</span>
                            <span>{ticket.contact_person}</span>
                          </div>
                        )}
                        {ticket.contact_email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <a href={`mailto:${ticket.contact_email}`} className="text-blue-600 hover:underline">
                              {ticket.contact_email}
                            </a>
                          </div>
                        )}
                        {ticket.contact_phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-gray-500" />
                            <a href={`tel:${ticket.contact_phone}`} className="text-blue-600 hover:underline">
                              {ticket.contact_phone}
                            </a>
                          </div>
                        )}
                        {ticket.asset_name && (
                          <div className="flex items-center gap-2 text-sm">
                            <Package className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold">Asset:</span>
                            <span>{ticket.asset_name}</span>
                          </div>
                        )}
                        {linkedAssets.length > 0 && (
                          <div className="text-sm space-y-1.5" data-testid="linked-assets-section">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-gray-500" />
                              <span className="font-semibold">Linked Assets:</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 ml-6">
                              {linkedAssets.map((la) => (
                                <span key={la.asset_id} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium" data-testid={`asset-status-${la.asset_id}`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${({active:'bg-emerald-500',maintenance:'bg-amber-500',inactive:'bg-gray-400',retired:'bg-red-500'})[la.status] || 'bg-gray-400'}`} title={la.status} />
                                  {la.asset_name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {linkedInvoices.length > 0 && (
                          <div className="text-sm space-y-1.5" data-testid="linked-invoices-section">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="font-semibold">Linked Invoices:</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5 ml-6">
                              {linkedInvoices.map((li) => (
                                <span 
                                  key={li.invoice_id} 
                                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium cursor-pointer hover:bg-blue-100 transition-colors"
                                  onClick={() => navigate(`/invoices/${li.invoice_id}`)}
                                  data-testid={`linked-invoice-${li.invoice_id}`}
                                >
                                  <FileText className="h-3 w-3" />
                                  {li.invoice_number} (${(li.total || 0).toFixed(2)})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold">Created:</span>
                          <span>{formatWithTimezone(ticket.created_at, 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold">Updated:</span>
                          <span>{formatWithTimezone(ticket.updated_at, 'MMM d, yyyy h:mm a')}</span>
                        </div>
                        {ticket.category && (
                          <div className="flex items-center gap-2 text-sm">
                            <Tag className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold">Category:</span>
                            <span className="capitalize">{ticket.category}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm">
                          {ticket.support_type === 'onsite' ? (
                            <MapPin className="h-4 w-4 text-purple-500" />
                          ) : (
                            <Monitor className="h-4 w-4 text-blue-500" />
                          )}
                          <span className="font-semibold">Support Type:</span>
                          <Badge 
                            variant="custom" 
                            className={
                              ticket.support_type === 'onsite' 
                                ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors' 
                                : ticket.support_type === 'hybrid'
                                ? 'bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors'
                                : 'bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors'
                            }
                          >
                            {ticket.support_type === 'onsite' ? 'On-Site Visit' : ticket.support_type === 'remote' ? 'Remote Support' : 'Hybrid'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Status, Priority, Assignment, and Board Controls */}
                {isAdminOrTech && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Status</Label>
                      <Select value={ticket.status} onValueChange={handleStatusChange}>
                        <SelectTrigger data-testid="detail-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Priority</Label>
                      <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                        <SelectTrigger data-testid="detail-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Assigned To</Label>
                      <Select 
                        value={ticket.assigned_to || 'unassigned'} 
                        onValueChange={handleAssignmentChange}
                      >
                        <SelectTrigger data-testid="detail-assignment-select">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {technicians.map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              {tech.full_name} ({tech.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold mb-2 block">Board</Label>
                      <Select
                        value={ticket.board_id || 'none'}
                        onValueChange={async (value) => {
                          try {
                            await api.put(`/tickets/${ticket.id}`, { board_id: value === 'none' ? null : value });
                            toast.success('Ticket moved to ' + (value === 'none' ? 'no board' : boards.find(b => b.id === value)?.name || 'board'));
                            loadTicketData();
                          } catch (error) {
                            toast.error('Failed to move ticket');
                          }
                        }}
                      >
                        <SelectTrigger data-testid="detail-board-select">
                          <SelectValue placeholder="No Board" />
                        </SelectTrigger>
                        <SelectContent className="co-scope ticket-ui">
                          <SelectItem value="none">No Board</SelectItem>
                          {boards.map(board => (
                            <SelectItem key={board.id} value={board.id}>{board.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* CC Contacts Section */}
                <Card data-testid="cc-contacts-section">
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      CC Contacts
                      {ccContacts.length > 0 && (
                        <Badge variant="outline" className="text-xs">{ccContacts.length}</Badge>
                      )}
                    </h3>
                    
                    {/* Existing CC contacts */}
                    {ccContacts.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {ccContacts.map((cc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5 text-sm"
                            data-testid={`cc-contact-${idx}`}
                          >
                            <Mail className="h-3 w-3 text-gray-500 shrink-0" />
                            <span className="truncate max-w-[200px]">
                              {cc.name ? `${cc.name} <${cc.email}>` : cc.email}
                            </span>
                            <button
                              onClick={() => handleRemoveCc(cc.email)}
                              className="ml-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                              data-testid={`remove-cc-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add CC form */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={ccEmail}
                        onChange={(e) => setCcEmail(e.target.value)}
                        className="flex-1"
                        data-testid="cc-email-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCc()}
                      />
                      <Input
                        type="text"
                        placeholder="Name (optional)"
                        value={ccName}
                        onChange={(e) => setCcName(e.target.value)}
                        className="flex-1 sm:max-w-[180px]"
                        data-testid="cc-name-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCc()}
                      />
                      <Button
                        size="sm"
                        onClick={handleAddCc}
                        disabled={ccAdding || !ccEmail.trim()}
                        className="shrink-0"
                        data-testid="add-cc-button"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        {ccAdding ? 'Adding...' : 'Add'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Ticket Description */}
                {ticket.description && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Description
                        {ticket.source === 'email' && (
                          <Badge variant="outline" className="ml-2 text-xs">From Email</Badge>
                        )}
                      </h3>
                      {(ticket.description_html || ticket.description?.includes('<div') || ticket.description?.includes('<p>')) ? (
                        <div 
                          className="ticket-description text-sm text-gray-700 break-words
                            [&_p]:my-2 [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all
                            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:my-3
                            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:my-2
                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                            [&_li]:my-1
                            [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2
                            [&_blockquote]:border-l-4 [&_blockquote]:border-blue-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-2
                            [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:rounded
                            [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:whitespace-pre-wrap [&_pre]:break-all"
                          style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticket.description_html || ticket.description, { blockRemoteImages: isRemoteBlocked('description') }) }}
                        />
                      ) : (
                        <div className="ticket-description text-sm text-gray-700 break-words" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderCommentHtml(ticket.description || ''), { blockRemoteImages: isRemoteBlocked('description') }) }}
                        />
                      )}
                      
                      {/* Link to original email */}
                      {ticket.email_message_id && (
                        <div className="mt-4 pt-3 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={handleViewOriginalEmail}
                            data-testid="view-original-email-btn"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            View Original Email
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="conversation" className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* ── Conversation Timeline (dominant) ── */}
              <div className="flex-1 min-w-0 space-y-4" data-testid="conversation-timeline">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Conversation</span>
                  <button
                    className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
                    onClick={() => setConvoSort(prev => prev === 'newest' ? 'oldest' : 'newest')}
                    data-testid="convo-sort-toggle"
                  >
                    {convoSort === 'newest' ? '↓ Newest first' : '↑ Oldest first'}
                  </button>
                </div>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-500">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="space-y-3">
                {/* Show initial description even with no comments */}
                {(ticket.description || ticket.description_html) && (
                  <Card className="border-blue-200 bg-blue-50/30" data-testid="ticket-initial-description">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm font-semibold">
                          {(ticket.customer_name || ticket.contact_person || ticket.created_by || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{ticket.customer_name || ticket.contact_person || ticket.created_by || 'Submitter'}</p>
                          <p className="text-xs text-gray-500">
                            {formatInCST(ticket.created_at, 'MMM d, yyyy h:mm a')}
                            <span className="ml-2 text-blue-600">• Original Description</span>
                          </p>
                        </div>
                      </div>
                      <div className="ticket-description prose prose-sm max-w-none text-sm">
                        {(ticket.description_html || ticket.description?.includes('<div') || ticket.description?.includes('<p>')) ? (
                          <QuotedReplyCollapse html={sanitizeHtml(ticket.description_html || ticket.description, { blockRemoteImages: isRemoteBlocked('description') })} className="" />
                        ) : (
                          <p className="whitespace-pre-wrap">{ticket.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">No replies yet. Start the conversation!</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Initial description — show at top for oldest-first, bottom for newest-first */}
                {convoSort === 'oldest' && (ticket.description || ticket.description_html) && (
                  <Card className="border-blue-200 bg-blue-50/30" data-testid="ticket-initial-description">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm font-semibold">
                            {(ticket.customer_name || ticket.contact_person || ticket.created_by || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{ticket.customer_name || ticket.contact_person || ticket.created_by || 'Submitter'}</p>
                            <p className="text-xs text-gray-500">
                              {formatInCST(ticket.created_at, 'MMM d, yyyy h:mm a')}
                              <span className="ml-2 text-blue-600">• Original Description</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="ticket-description prose prose-sm max-w-none text-sm">
                        {(ticket.description_html || ticket.description?.includes('<div') || ticket.description?.includes('<p>')) ? (
                          <QuotedReplyCollapse html={sanitizeHtml(ticket.description_html || ticket.description, { blockRemoteImages: isRemoteBlocked('description') })} className="" />
                        ) : (
                          <p className="whitespace-pre-wrap">{ticket.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {(convoSort === 'newest' ? [...comments].reverse() : comments).map((comment) => (
                  <Card 
                    key={comment.id} 
                    className={comment.is_internal ? 'border-orange-200 bg-orange-50' : ''}
                    data-testid="ticket-comment"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                            {comment.author_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{comment.author_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatInCST(comment.created_at, 'MMM d, yyyy h:mm a')}
                              {comment.author_role && (
                                <span className="ml-2 text-blue-600 capitalize">• {comment.author_role}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {comment.is_internal && (
                          <Badge className="bg-orange-100 text-orange-800">
                            <Lock className="h-3 w-3 mr-1" />
                            Internal Note
                          </Badge>
                        )}
                      </div>
                      {/* Per-message (session-only) external-image control */}
                      {hasRemoteImages([renderCommentHtml(comment.comment_text || '')]) && (
                        isRemoteBlocked(comment.id) ? (
                          <div data-testid="comment-image-privacy-banner" className="ml-10 mb-2 flex items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">
                            <span className="flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5 shrink-0" /> External images in this reply are hidden for your privacy.</span>
                            <Button size="sm" variant="outline" onClick={() => toggleMessageImages(comment.id, true)} data-testid="comment-load-images-btn" className="h-6 px-2 shrink-0 border-amber-500/50 text-amber-100 hover:bg-amber-500/20">Load pictures</Button>
                          </div>
                        ) : (
                          <div data-testid="comment-images-trusted-note" className="ml-10 mb-2 flex items-center gap-2 text-xs text-gray-400">
                            <ShieldCheck className="h-3.5 w-3.5" /> External images shown (this session only).
                            <Button size="sm" variant="ghost" onClick={() => toggleMessageImages(comment.id, false)} data-testid="comment-hide-images-btn" className="h-6 px-2 text-xs">Hide pictures</Button>
                          </div>
                        )
                      )}
                      {/* Render comment with quoted-reply collapsing */}
                      <QuotedReplyCollapse
                        html={sanitizeHtml(renderCommentHtml(comment.comment_text || ''), { blockRemoteImages: isRemoteBlocked(comment.id) })}
                        className="ticket-description text-sm text-gray-700 ml-10 prose prose-sm max-w-none break-words overflow-hidden
                          prose-p:my-1 prose-a:text-blue-600 prose-a:underline prose-headings:my-2
                          [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-2
                          [&_a]:text-blue-600 [&_a]:underline [&_a]:break-all"
                      />
                    </CardContent>
                  </Card>
                ))}
                {/* Initial description at bottom for newest-first */}
                {convoSort === 'newest' && (ticket.description || ticket.description_html) && (
                  <Card className="border-blue-200 bg-blue-50/30">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-sm font-semibold">
                            {(ticket.customer_name || ticket.contact_person || ticket.created_by || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{ticket.customer_name || ticket.contact_person || ticket.created_by || 'Submitter'}</p>
                            <p className="text-xs text-gray-500">
                              {formatInCST(ticket.created_at, 'MMM d, yyyy h:mm a')}
                              <span className="ml-2 text-blue-600">• Original Description</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="ticket-description prose prose-sm max-w-none text-sm">
                        {(ticket.description_html || ticket.description?.includes('<div') || ticket.description?.includes('<p>')) ? (
                          <QuotedReplyCollapse html={sanitizeHtml(ticket.description_html || ticket.description, { blockRemoteImages: isRemoteBlocked('description') })} className="" />
                        ) : (
                          <p className="whitespace-pre-wrap">{ticket.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Add Comment Form */}
            <Card>
              <CardContent className="p-4">
                <form onSubmit={handleAddComment} className="space-y-3">
                  <div>
                    <Label htmlFor="comment" className="text-sm font-semibold mb-2 block">
                      Add Comment
                    </Label>
                    <RichTextEditor
                      content={newComment}
                      onChange={setNewComment}
                      placeholder="Type your comment here... Use the toolbar for formatting."
                      minHeight="120px"
                    />
                  </div>
                  
                  {isAdminOrTech && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal"
                        data-testid="internal-checkbox"
                        checked={isInternal}
                        onCheckedChange={setIsInternal}
                      />
                      <Label 
                        htmlFor="internal" 
                        className="text-sm cursor-pointer flex items-center gap-1"
                      >
                        <Lock className="h-3 w-3" />
                        Internal note (only visible to staff)
                      </Label>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isAdminOrTech && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          data-testid="attach-invoice-btn"
                          onClick={() => { loadCustomerInvoices(); setShowInvoicePicker(!showInvoicePicker); }}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          Attach Invoice
                        </Button>
                      )}
                    </div>
                    <Button 
                      type="submit" 
                      disabled={submitting || !newComment || newComment === '<p></p>'}
                      data-testid="submit-comment-button"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? 'Sending...' : 'Add Comment'}
                    </Button>
                  </div>

                  {/* Invoice Picker */}
                  {showInvoicePicker && (
                    <div className="border rounded-lg p-3 bg-gray-50 space-y-2" data-testid="invoice-picker">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Select an invoice to attach as PDF</Label>
                        <Button variant="ghost" size="sm" className="h-6" onClick={() => setShowInvoicePicker(false)}><X className="h-3 w-3" /></Button>
                      </div>
                      {customerInvoices.length === 0 ? (
                        <p className="text-xs text-gray-500 py-2">No invoices found for this customer.</p>
                      ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {customerInvoices.map(inv => (
                            <button
                              key={inv.id}
                              type="button"
                              className="w-full flex items-center justify-between px-3 py-2 text-left text-sm rounded hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
                              onClick={() => handleAttachInvoicePdf(inv.id, inv.invoice_number)}
                              disabled={attachingInvoice}
                              data-testid={`attach-invoice-${inv.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-3.5 w-3.5 text-gray-400" />
                                <span className="font-mono">{inv.invoice_number}</span>
                                <Badge variant="custom" className={`text-[10px] ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{inv.status}</Badge>
                              </div>
                              <span className="text-xs font-semibold">${typeof fmtMoney === 'function' ? fmtMoney(inv.total || 0) : (inv.total || 0).toFixed(2)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {attachingInvoice && <p className="text-xs text-blue-600 animate-pulse">Generating and attaching PDF...</p>}
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
              </div>

              {/* ── Metadata Sidebar ── */}
              <div className="lg:w-80 flex-shrink-0 space-y-3" data-testid="ticket-sidebar">
                {/* Quick Actions */}
                {isAdminOrTech && (
                  <Card>
                    <CardContent className="p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Quick Actions</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[11px] text-gray-500">Status</Label>
                          <Select value={ticket.status} onValueChange={handleStatusChange}>
                            <SelectTrigger className="h-8 text-xs" data-testid="sidebar-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="co-scope ticket-ui">
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-gray-500">Priority</Label>
                          <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                            <SelectTrigger className="h-8 text-xs" data-testid="sidebar-priority-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="co-scope ticket-ui">
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] text-gray-500">Assigned To</Label>
                        <Select value={ticket.assigned_to || 'unassigned'} onValueChange={handleAssignmentChange}>
                          <SelectTrigger className="h-8 text-xs" data-testid="sidebar-assign-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="co-scope ticket-ui">
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Customer & Contact */}
                <Card>
                  <CardContent className="p-3 space-y-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</h4>
                    {ticket.customer_name && (
                      <RecordLink
                        to={recordHref('customer', ticket.customer_id, { tab: 'tickets' })}
                        className="text-sm font-medium text-blue-600 hover:underline text-left block"
                        fallbackClassName="text-sm font-medium text-left block"
                        data-testid="sidebar-customer-link"
                      >
                        {ticket.customer_name}
                      </RecordLink>
                    )}
                    {ticket.contact_person && <p className="text-xs text-gray-600"><UserCog className="h-3 w-3 inline mr-1" />{ticket.contact_person}</p>}
                    {ticket.contact_email && <p className="text-xs"><Mail className="h-3 w-3 inline mr-1 text-gray-400" /><a href={`mailto:${ticket.contact_email}`} className="text-blue-600 hover:underline">{ticket.contact_email}</a></p>}
                    {ticket.contact_phone && <p className="text-xs"><Phone className="h-3 w-3 inline mr-1 text-gray-400" /><a href={`tel:${ticket.contact_phone}`} className="text-blue-600 hover:underline">{ticket.contact_phone}</a></p>}
                  </CardContent>
                </Card>

                {/* Asset & Links */}
                {(ticket.asset_name || linkedAssets.length > 0) && (
                  <Card>
                    <CardContent className="p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assets</h4>
                      {ticket.asset_id && (
                        <button onClick={() => navigate(`/assets/${ticket.asset_id}`)}
                          className="text-sm text-blue-600 hover:underline text-left flex items-center gap-1" data-testid="sidebar-asset-link">
                          <Package className="h-3 w-3" /> {ticket.asset_name}
                        </button>
                      )}
                      {linkedAssets.map(la => (
                        <button key={la.asset_id} onClick={() => navigate(`/assets/${la.asset_id}`)}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <Package className="h-3 w-3" /> {la.asset_name}
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Provider Info (Ninja-backed tickets) */}
                {(ticket.source === 'ninjaone' || ticket.source === 'ninjarmm_webhook') && (
                  <Card className="border-blue-200">
                    <CardContent className="p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider">NinjaOne</h4>
                      {ticket.provider_ticket_id && <p className="text-xs text-gray-600">Ticket ID: <span className="font-mono">{ticket.provider_ticket_id}</span></p>}
                      {ticket.ninjarmm_device_id && <p className="text-xs text-gray-600">Device: <span className="font-mono">{ticket.ninjarmm_device_id}</span></p>}
                      {ticket.provider_deep_link && (
                        <a href={ticket.provider_deep_link} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="sidebar-ninja-link">
                          <ExternalLink className="h-3 w-3" /> Open in NinjaOne
                        </a>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Timestamps */}
                <Card>
                  <CardContent className="p-3 space-y-1.5">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</h4>
                    <p className="text-xs text-gray-600"><Calendar className="h-3 w-3 inline mr-1" />Created: {formatWithTimezone(ticket.created_at, 'MMM d, yyyy h:mm a')}</p>
                    <p className="text-xs text-gray-600"><Clock className="h-3 w-3 inline mr-1" />Updated: {formatWithTimezone(ticket.updated_at, 'MMM d, yyyy h:mm a')}</p>
                    {ticket.category && <p className="text-xs text-gray-600"><Tag className="h-3 w-3 inline mr-1" />Category: <span className="capitalize">{ticket.category}</span></p>}
                    {ticket.board_id && <p className="text-xs text-gray-600">Board: {boards.find(b => b.id === ticket.board_id)?.name || ticket.board_id}</p>}
                  </CardContent>
                </Card>

                {/* Linked Invoices */}
                {linkedInvoices.length > 0 && (
                  <Card>
                    <CardContent className="p-3 space-y-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoices</h4>
                      {linkedInvoices.map(li => (
                        <button key={li.invoice_id} onClick={() => navigate(`/invoices/${li.invoice_id}`)}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`sidebar-invoice-${li.invoice_id}`}>
                          <FileText className="h-3 w-3" /> {li.invoice_number} (${(li.total || 0).toFixed(2)})
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="attachments" className="space-y-4">
            {/* Upload Section */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">Upload Attachment</h3>
                    <p className="text-sm text-gray-600">Maximum file size: 10MB</p>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-button"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Embedded Images (extracted from email body/replies) */}
            <EmbeddedImagesPanel
              images={inlineImages}
              ticketNumber={ticket.ticket_number}
              promotedKeys={promotedImageKeys}
              onPromote={handlePromoteImage}
              customerId={ticket.customer_id}
              customerName={ticket.customer_name}
              onOpen={(i) => setLightbox({ images: inlineImages, index: i })}
            />

            {/* Attachments List */}
            {attachments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Paperclip className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No attachments yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((attachment) => (
                  <Card key={attachment.id} data-testid="attachment-item">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                            <Paperclip className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.file_size)} • Uploaded by {attachment.uploaded_by_name}
                              {' • '}{formatInCST(attachment.created_at, 'MMM d, yyyy')}
                              {attachment.source === 'email' && (
                                <Badge className="ml-2 bg-purple-100 text-purple-800">From Email</Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(attachment.id, attachment.filename)}
                            data-testid="download-attachment-button"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              data-testid="delete-attachment-button"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Billing Tab */}
          {isAdminOrTech && (
            <TabsContent value="billing" className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Time Tracker */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Timer className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-lg">Time Tracker</h3>
                            <p className="text-sm text-gray-600">
                              {activeTimer ? 'Timer running...' : 'Clock in to start tracking time'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {activeTimer && (
                            <div className="text-right">
                              <div className="text-3xl font-mono font-bold text-blue-600">
                                {formatElapsedTime(elapsedTime)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Started {formatInCST(activeTimer.start_time, 'h:mm a')} CST
                              </div>
                            </div>
                          )}
                          {activeTimer ? (
                            <Button
                              size="lg"
                              variant="destructive"
                              onClick={handleClockOut}
                              data-testid="clock-out-button"
                            >
                              <StopCircle className="h-5 w-5 mr-2" />
                              Clock Out
                            </Button>
                          ) : (
                            <Button
                              size="lg"
                              onClick={handleClockIn}
                              data-testid="clock-in-button"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <PlayCircle className="h-5 w-5 mr-2" />
                              Clock In
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Time Entries History */}
                    {timeEntries.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Time Entries ({timeEntries.length})
                        </h3>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {timeEntries.map((entry) => (
                            <Card key={entry.id} className="border-l-4 border-l-blue-500">
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-sm">{entry.technician_name}</span>
                                      {entry.is_billable && (
                                        <Badge className="bg-green-100 text-green-800 text-xs">Billable</Badge>
                                      )}
                                      {!entry.end_time && (
                                        <Badge className="bg-blue-100 text-blue-800 text-xs animate-pulse">Active</Badge>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>
                                        {formatInCST(entry.start_time, 'MMM d, yyyy h:mm a')}
                                        {entry.end_time && (
                                          <span> → {formatInCST(entry.end_time, 'h:mm a')}</span>
                                        )}
                                      </div>
                                      {entry.duration_hours && (
                                        <div className="font-semibold text-blue-600">
                                          Duration: {formatDuration(entry.duration_hours)}
                                        </div>
                                      )}
                                      {entry.notes && (
                                        <div className="text-gray-500 italic">{entry.notes}</div>
                                      )}
                                    </div>
                                  </div>
                                  {isAdmin && entry.end_time && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteTimeEntry(entry.id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-700">Total Tracked Time:</span>
                            <span className="text-xl font-bold text-blue-600">
                              {formatDuration(timeEntries.reduce((sum, e) => sum + (e.duration_hours || 0), 0))}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Separator />

                    {/* Billable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-6 w-6 text-green-600" />
                        <div>
                          <h3 className="font-semibold">Billable Ticket</h3>
                          <p className="text-sm text-gray-600">
                            Mark this ticket as billable if not covered under contract/SLA
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        checked={billingData.is_billable}
                        onCheckedChange={(checked) => setBillingData({...billingData, is_billable: checked})}
                        data-testid="billable-checkbox"
                        className="h-6 w-6"
                      />
                    </div>

                    {billingData.is_billable && (
                      <>
                        <Separator />
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <Label htmlFor="billable_hours" className="text-sm font-semibold mb-2 block">
                              Hours Worked
                            </Label>
                            <Input
                              id="billable_hours"
                              type="number"
                              step="0.25"
                              min="0"
                              placeholder="0.00"
                              value={billingData.billable_hours}
                              onChange={(e) => setBillingData({...billingData, billable_hours: e.target.value})}
                              data-testid="billable-hours-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter time in hours (e.g., 2.5 for 2.5 hours)</p>
                          </div>

                          <div>
                            <Label htmlFor="hourly_rate" className="text-sm font-semibold mb-2 block">
                              Hourly Rate ($)
                            </Label>
                            <Input
                              id="hourly_rate"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={billingData.hourly_rate}
                              onChange={(e) => setBillingData({...billingData, hourly_rate: e.target.value})}
                              data-testid="hourly-rate-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Standard rate or custom rate for this ticket</p>
                          </div>
                        </div>

                        {/* Calculated Time Amount Display */}
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-5 w-5 text-blue-600" />
                                <span className="font-semibold text-gray-700">Time Billable Amount:</span>
                              </div>
                              <span className="text-2xl font-bold text-blue-600" data-testid="billable-amount">
                                ${billingData.billable_amount || '0.00'}
                              </span>
                            </div>
                            {billingData.billable_hours && billingData.hourly_rate && (
                              <p className="text-xs text-gray-600 mt-2">
                                {billingData.billable_hours} hours × ${billingData.hourly_rate}/hour
                              </p>
                            )}
                          </CardContent>
                        </Card>

                        <div className="flex justify-end gap-2">
                          <Button onClick={handleBillingUpdate} data-testid="save-billing-button">
                            <Save className="h-4 w-4 mr-2" />
                            Save Billing
                          </Button>
                          <Button variant="outline" onClick={handleCreateInvoiceFromBilling} data-testid="create-invoice-from-billing-btn" disabled={!billingData.billable_hours || !billingData.hourly_rate}>
                            <FileText className="h-4 w-4 mr-2" />
                            Create Invoice
                          </Button>
                        </div>
                      </>
                    )}

                    {!billingData.is_billable && (
                      <div className="text-center py-8 text-gray-500">
                        <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <p>This ticket is not marked as billable</p>
                        <p className="text-sm mt-1">Enable the toggle above if this work should be billed</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Mileage Tab */}
          {isAdminOrTech && (
            <TabsContent value="mileage" className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Mileage Tracking Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Car className="h-6 w-6 text-blue-600" />
                        <div>
                          <h3 className="font-semibold">Mileage Tracking</h3>
                          <p className="text-sm text-gray-600">
                            Track travel for on-site support visits
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="mileage" className="text-sm font-semibold mb-2 block">
                          Miles Traveled
                        </Label>
                        <Input
                          id="mileage"
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="0.0"
                          value={billingData.mileage}
                          onChange={(e) => setBillingData({...billingData, mileage: e.target.value})}
                          data-testid="mileage-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Round trip distance</p>
                      </div>

                      <div>
                        <Label htmlFor="mileage_rate" className="text-sm font-semibold mb-2 block">
                          Rate per Mile ($)
                        </Label>
                        <Input
                          id="mileage_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.67"
                          value={billingData.mileage_rate}
                          onChange={(e) => setBillingData({...billingData, mileage_rate: e.target.value})}
                          data-testid="mileage-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">IRS standard: $0.67/mile</p>
                      </div>

                      <div className="flex items-end">
                        <div className="w-full">
                          <Label className="text-sm font-semibold mb-2 block">
                            Mileage Cost
                          </Label>
                          <div className="h-10 px-3 rounded-md border bg-gray-50 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">
                              ${billingData.mileage_amount || '0.00'}
                            </span>
                            <Checkbox
                              checked={billingData.mileage_billable}
                              onCheckedChange={(checked) => setBillingData({...billingData, mileage_billable: checked})}
                              data-testid="mileage-billable-checkbox"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {billingData.mileage_billable ? '✓ Bill to customer' : 'Internal cost only'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {billingData.mileage && billingData.mileage_rate && (
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <Car className="h-3 w-3 inline mr-1" />
                        {billingData.mileage} miles × ${billingData.mileage_rate}/mile = ${billingData.mileage_amount || '0.00'}
                        {billingData.mileage_billable && <span className="text-green-700 font-semibold ml-2">(Billable to customer)</span>}
                        {!billingData.mileage_billable && <span className="text-gray-500 ml-2">(Internal tracking only)</span>}
                      </div>
                    )}

                    {/* Mileage Summary */}
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Car className="h-5 w-5 text-green-600" />
                            <span className="font-semibold text-gray-700">Mileage Cost:</span>
                          </div>
                          <span className="text-2xl font-bold text-green-600" data-testid="mileage-total">
                            ${billingData.mileage_amount || '0.00'}
                          </span>
                        </div>
                        {billingData.mileage && billingData.mileage_rate && (
                          <p className="text-xs text-gray-600 mt-2">
                            {billingData.mileage} miles × ${billingData.mileage_rate}/mile
                            {billingData.mileage_billable 
                              ? <span className="text-green-700 font-semibold ml-2">• Will be billed to customer</span>
                              : <span className="text-gray-600 ml-2">• Internal tracking only (not billed)</span>
                            }
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {!billingData.mileage && (
                      <div className="text-center py-8 text-gray-500">
                        <Car className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                        <p>No mileage tracked for this ticket</p>
                        <p className="text-sm mt-1">Enter miles above if this required on-site travel</p>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button onClick={handleBillingUpdate} data-testid="save-mileage-button">
                        <Save className="h-4 w-4 mr-2" />
                        Save Mileage Information
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Combined Total Summary (shown below tabs when both time and mileage are billable) */}
        {isAdminOrTech && billingData.is_billable && (billingData.billable_amount > 0 || (billingData.mileage_billable && billingData.mileage_amount > 0)) && (
          <Card className="bg-purple-50 border-purple-200 mt-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-gray-700">Total Billable to Customer:</span>
                </div>
                <span className="text-3xl font-bold text-purple-600">
                  ${calculateTotalBillable()}
                </span>
              </div>
              <div className="mt-2 space-y-1">
                {billingData.billable_hours && billingData.hourly_rate && (
                  <p className="text-xs text-gray-600">
                    Time: {billingData.billable_hours} hours × ${billingData.hourly_rate}/hour = ${billingData.billable_amount || '0.00'}
                  </p>
                )}
                {billingData.mileage_billable && billingData.mileage && billingData.mileage_rate && (
                  <p className="text-xs text-gray-600">
                    Mileage: {billingData.mileage} miles × ${billingData.mileage_rate}/mile = ${billingData.mileage_amount || '0.00'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Original Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="co-scope max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="original-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Original Email
            </DialogTitle>
          </DialogHeader>
          {originalEmail && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-y-1 text-sm border rounded-lg p-3 bg-gray-50">
                <span className="text-gray-500 font-medium">From:</span>
                <span>{originalEmail.sender_name} &lt;{originalEmail.sender_email}&gt;</span>
                <span className="text-gray-500 font-medium">Subject:</span>
                <span className="font-semibold">{originalEmail.subject}</span>
                <span className="text-gray-500 font-medium">Date:</span>
                <span>{originalEmail.received_date ? new Date(originalEmail.received_date).toLocaleString() : ''}</span>
                {originalEmail.to_recipients?.length > 0 && (
                  <>
                    <span className="text-gray-500 font-medium">To:</span>
                    <span>{originalEmail.to_recipients.map(r => r.email || r).join(', ')}</span>
                  </>
                )}
              </div>
              <div className="border rounded-lg overflow-hidden">
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;font-size:14px;color:#333;line-height:1.5;}img{max-width:100%;height:auto;}a{color:#0078d4;}</style></head><body>${sanitizeHtml(originalEmail.body_html || '') || originalEmail.body_preview || 'No content'}</body></html>`}
                  className="w-full border-0"
                  style={{ minHeight: '300px', height: '500px' }}
                  title="Original Email"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

